import React, { useEffect, useState } from 'react';

import colors from 'theme/_colors.scss';
import { CHART_PLUGINS } from 'sharedComponents/Chart/constants';
import { translate } from 'helpers/translateHelper';
import { convertHistorgramData } from './helpers';
import { afterChartReDraw, chartLegendItemClick } from '../../../../helpers';
import { renderToStaticMarkup } from 'react-dom/server';
import { HistogramTooltip } from 'sharedComponents/ChartComponents/Histogram';
import { RemovableLegend } from 'sharedComponents/ChartComponents/CommonLegend';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';


export const chartPlugins = [CHART_PLUGINS.HIGHCHARTS_MORE, CHART_PLUGINS.NO_DATA_TO_DISPLAY];

const useChartConfig = (chartData, removeFn) => {

    const [options, setOptions] = useState({});
    useEffect(() => {

        const histogramData = convertHistorgramData(chartData);
        const { components, categories, xUnit, yUnit, maxValue } = histogramData;
        // no data: hide the axes and size the chart to fit the legend items
        const hasData = categories.length > 0;
        const noDataChartHeight = Math.max(340, 300 + components.length * 34);

        setOptions({
            chart: {
                type: 'column',
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
                zoomType: 'y',
                height: hasData ? '600' : noDataChartHeight,
                events: {
                    redraw: function (chartInstance) {
                        const visibleCount = this?.series?.filter(signal => signal.visible).length;
                        if (visibleCount === 0 && this?.series?.length === 1) {
                            this.series[0]?.show();
                        }
                        afterChartReDraw(chartInstance.target.container.id, removeFn);
                    }
                },
                marginBottom: hasData ? 350 : 60,
                spacingTop: 20,
                marginRight: 20
            },
            title: {
                text: ''
            },
            xAxis: [
                {
                    categories,
                    startOnTick: true,
                    endOnTick: true,
                    title: {
                        text: xUnit,
                    },
                    gridLineWidth: 1,
                    id: 'xAxis',
                    visible: hasData,
                }
            ],
            yAxis: {
                max: Math.ceil(maxValue / 10) * 10,
                tickInterval: 10,
                title: {
                    text: yUnit,
                    rotation: 270,
                },
                gridLineWidth: 1,
                id: 'yAxis',
                visible: hasData,
            },
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 1,
                    groupPadding: 0,
                    shadow: false
                },
                series: {
                    events: {
                        legendItemClick: chartLegendItemClick
                    }
                }
            },
            lang: {
                noData: renderToStaticMarkup(<ChartNoDataText
                    title={translate('ABB.Powertrain.Frontend.noDataLabelAvailable')}
                    text={translate('ABB.Powertrain.Frontend.adjustDateRangeOrChartType')} />)
            },
            noData: {
                useHTML: true
            },
            series: components,
            tooltip: {
                style: {
                    fontSize: '14px',
                    lineHeight: '20px'
                },
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                formatter: function () {
                    return renderToStaticMarkup(
                        <HistogramTooltip
                            component={this.series.userOptions.component}
                            name={this.series.name}
                            x={this.x}
                            y={this.y}
                            {...{ xUnit, yUnit }}
                        />);
                }
            },
            legend: {
                useHTML: true,
                layout: 'vertical',
                align: 'left',
                enabled: true,
                floating: true,
                reversed: true,
                verticalAlign: 'top',
                itemDistance: 16,
                itemMarginTop: 10,
                navigation: {
                    enabled: false
                },
                itemStyle: {
                    fontSize: '14px',
                    fontFamily: 'ABBvoice-Regular',
                    letterSpacing: '0',
                    lineHeight: '17px',
                },
                symbolRadius: 0,
                squareSymbol: false,
                symbolHeight: 8,
                symbolWidth: 8,
                y: hasData ? 300 : 280,
                labelFormatter: function () {
                    return renderToStaticMarkup(
                        <RemovableLegend
                            signalSourceName={this.userOptions.component}
                            legend={this}
                            className='cross-asset__legend'
                        />);
                }
            },
            credits: {
                enabled: false
            },
            navigation: {
                buttonOptions: {
                    theme: {
                        states: {
                            hover: {
                                fill: colors.white,
                                stroke: 'none'
                            },
                            select: {
                                fill: colors.white,
                                select: 'none'
                            }
                        }
                    }
                }
            },
            exporting: {
                enabled: false,
            }
        });

    }, [chartData]);

    return options;
};

export default useChartConfig;
